/**
 * The referral programme's words and arithmetic, in one place for the API that
 * pays rewards and both screens that show them. Pure: nothing here reads a
 * database or a clock it was not handed.
 */

/**
 * The cookie an invite link leaves behind, read by the API when the account is
 * created. First-party on the client app's own host, which is also where
 * `/api/` is served, so the sign-up request carries it without any help.
 */
export const REFERRAL_COOKIE = 'snily_ref';

/** How long a click keeps counting: long enough to think it over, not for ever. */
export const REFERRAL_COOKIE_DAYS = 30;

/** The invite link's path on the client app: `/r/<code>`. */
export const REFERRAL_PATH = '/r';

/**
 * What an invite code may look like. Eight characters from an alphabet with no
 * 0/O or 1/I/L, so a code read aloud or copied from a screenshot comes out the
 * same; checked on the way in so a cookie cannot carry anything else.
 */
export const REFERRAL_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const REFERRAL_CODE_LENGTH = 8;
const CODE_SHAPE = new RegExp(`^[${REFERRAL_CODE_ALPHABET}]{${REFERRAL_CODE_LENGTH}}$`);

/** A code as it arrived — a cookie, a URL — normalised, or null when it is not one. */
export function referralCodeOf(raw: string | null | undefined): string | null {
  const code = raw?.trim().toUpperCase();
  return code && CODE_SHAPE.test(code) ? code : null;
}

/** A fresh code from whatever random source the caller has. */
export function drawReferralCode(random: () => number): string {
  let code = '';
  for (let i = 0; i < REFERRAL_CODE_LENGTH; i += 1) {
    code += REFERRAL_CODE_ALPHABET[Math.floor(random() * REFERRAL_CODE_ALPHABET.length)];
  }
  return code;
}

/** The numbers a manager sets in /settings, as the programme reads them today. */
export interface ReferralTerms {
  /** Share of each payment owed to the inviter, whole percent. */
  rewardPercent: number;
  /** How many months after the first payment keep earning. */
  rewardMonths: number;
  /** Off the invited client's first paid month, whole percent. */
  welcomePercent: number;
  /** Days a reward waits out the refund window before it is credited. */
  holdDays: number;
}

/** The share of a payment owed, in whole cents, never more than was paid. */
export function rewardCents(paidCents: number, percent: number): number {
  if (paidCents <= 0 || percent <= 0) return 0;
  return Math.min(paidCents, Math.floor((paidCents * percent) / 100));
}

/**
 * Whether a payment made at `paidAt` still earns: inside `months` calendar
 * months of the first payment. The first payment itself always does.
 */
export function withinRewardWindow(firstPaidAt: Date, paidAt: Date, months: number): boolean {
  const end = new Date(firstPaidAt);
  end.setUTCMonth(end.getUTCMonth() + months);
  return paidAt.getTime() >= firstPaidAt.getTime() && paidAt.getTime() < end.getTime();
}

/** When a reward becomes credit: the payment plus the hold. */
export function rewardAvailableAt(paidAt: Date, holdDays: number): Date {
  return new Date(paidAt.getTime() + holdDays * 24 * 60 * 60 * 1000);
}

/**
 * An invited person as the inviter sees them: enough to recognise a friend,
 * not enough to hand anybody a list of addresses. `anna.k@gmail.com` reads
 * `an•••@gmail.com`.
 */
export function maskedEmail(email: string | null | undefined): string {
  if (!email) return 'Invited client';
  const [name, domain] = email.split('@');
  if (!domain) return 'Invited client';
  return `${name.slice(0, 2)}•••@${domain}`;
}

/** Dollars from cents, for a label: `$6.90`. */
export function centsLabel(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
